import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// d.ts
import { DCRecord, IDataController } from "@/@types/lib/dataController"

// Custom Components
import ModelTable from '@/ui/Table/ModelTable';
import TableButtonEdit from '@/ui/Table/ActionButtons/TableButtonEdit';
import TableButtonView from '@/ui/Table/ActionButtons/TableButtonView';
import DialogContext from '@/ui/DialogContext/DialogContext';
import LoaderContext from '@/components/LoaderContext/LoaderContext';
import UserDialog from './UserDialog';

import model from '@/models/user';
import dataController from '@/lib/dataController';
import { DialogContextType } from '@/@types/ui/DialogContext';

import { LoaderContextType } from '@/@types/context/LoaderContext';
import UserContext from '@/components/UserContext/UserContext';
import { Permissions } from '@/lib/permissions';
import { OwnerOrganisations } from '@/lib/ownerOrganisations';

const UsersTable = () => {
  const dialogContext = useContext<DialogContextType>(DialogContext);
  const loaderContext = useContext(LoaderContext) as LoaderContextType;
  const userContext = useContext(UserContext);

  const { t } = useTranslation();

  const [records, setRecords] = useState<DCRecord[]>([]);

  const dc: IDataController = new dataController(model);

  useEffect(() => {
    refreshRecords();
  }, []);


  const getInsertForm = () => {
    if (userContext?.hasAnyPermission([Permissions.Administration])) {
      return 'insert';
    } else if (userContext?.hasAnyPermission([Permissions.AdministrationOrgUsers])) {
      switch (userContext?.organisationId) {
        case OwnerOrganisations.HPS.code:
          return 'insert_hps';
        case OwnerOrganisations.HGSS.code:
          return 'insert_hgss';
        default:
          return 'default';
      }
    } else {
      return 'default';
    }
  }

  const getUpdateForm = () => {
    if (userContext?.hasAnyPermission([Permissions.Administration])) {
      return 'update';
    } else if (userContext?.hasAnyPermission([Permissions.AdministrationOrgUsers])) {
      switch (userContext?.organisationId) {
        case OwnerOrganisations.HPS.code:
          return 'update_hps';
        case OwnerOrganisations.HGSS.code:
          return 'update_hgss';
        default:
          return 'default';
      }
    } else {
      return 'default';
    }
  }

  const handleAdd = () => {
    const insertForm = getInsertForm();

    dialogContext.showDialog(UserDialog, {
      dc,
      mode: 'insert',
      form: insertForm,
      onClose: handleDialogClose,
    });
  };

  const handleEdit = (evt: React.SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];
    const updateForm = getUpdateForm();

    dialogContext.showDialog(UserDialog, {
      dc,
      mode: 'update',
      form: updateForm,
      recordId: record.id,
      onClose: handleDialogClose,
    });
  };

  const handleView = (evt: React.SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];
    const updateForm = getUpdateForm();

    dialogContext.showDialog(UserDialog, {
      dc,
      mode: 'view',
      form: updateForm,
      recordId: record.id,
      onClose: handleDialogClose,
    });
  };

  const handleDialogClose = (result: { dataChanged: boolean; action: string }) => {
    if (result.dataChanged) {
      refreshRecords();
    }
  };

  const refreshRecords = () => {
    loaderContext.toggleLoading(true);
    dc.GetData().then((resp) => {
        if (resp.success) {
          if (Array.isArray(resp.data)) {
            setRecords(resp.data);
          } else {
            setRecords([]);
          }
        }
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  const getViewName = () => {
    if (userContext?.hasAnyPermission([Permissions.Administration])) {
      return "admin"
    } else if (userContext?.hasAnyPermission([Permissions.AdministrationOrgUsers])) {
      if (userContext.organisationId === OwnerOrganisations.HGSS.code) {
        return "hgss";
      } else if (userContext.organisationId === OwnerOrganisations.HPS.code) {
        return "hps";
      } else {
        return "default";
      }
    } else {
      return "default";
    }
  }

  const viewName = getViewName();

  console.log(viewName);

  return (
    <ModelTable
      title={t('titles.users')}
      records={records}
      dc={dc}
      viewName={viewName}
      allowSelection="one"
      allowFilter
      allowExport
      allowAdd
      addLabel={t('buttons.new_m')}
      handleAdd={handleAdd}
      onRowAction={() => {}}
      allowRowAction={() => true}
    >
      <TableButtonEdit variant="outlined" onClick={handleEdit} />
      <TableButtonView variant="contained" onClick={handleView} />
    </ModelTable>
  );
};

export default UsersTable;
